/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Card, Chip, CircularProgress, IconButton,
  Table, TableBody, TableCell, TableContainer, TableRow,
  TextField, InputAdornment, Select, MenuItem, FormControl, InputLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PaymentIcon from '@mui/icons-material/Payment';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import NoteIcon from '@mui/icons-material/Note';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import { useNavigate } from 'react-router-dom';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import { api } from '../../../api';
import { usePharmacy } from '../../../contexts/PharmacyContext';
import { useListUrlState } from '../../../hooks/useListUrlState';
import { useListKeyboard, ACTIVE_ROW_SX } from 'hooks/useListKeyboard';

const TEAL = '#00798E';
const TH = {
  py: 1.5, px: 2, textTransform: 'uppercase', fontSize: '0.6rem',
  fontWeight: 700, color: '#999', borderBottom: '1.5px solid #f0f2f5',
  whiteSpace: 'nowrap', letterSpacing: '0.06em',
};
const td = (last) => ({
  py: 1.25, px: 2, borderBottom: last ? 'none' : '1px solid rgba(0,0,0,0.05)', fontSize: '0.8rem',
});

const STATUS_COLOR = {
  DRAFT: 'default', FINALIZED: 'success', CANCELLED: 'error', PARTIALLY_RETURNED: 'warning',
};
const STATUS_LABEL = {
  DRAFT: 'Draft', FINALIZED: 'Finalized', CANCELLED: 'Cancelled', PARTIALLY_RETURNED: 'Part. Returned',
};

const fmt = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

function BillingList() {
  const navigate = useNavigate();
  const { selectedPharmacy } = usePharmacy();
  const { readParams, writeParams } = useListUrlState();
  const urlParams = readParams();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState(urlParams?.get('search') || '');
  const [status, setStatus] = useState(urlParams?.get('status') || '');
  const [page, setPage] = useState(urlParams ? Math.max(parseInt(urlParams.get('page'), 10) - 1, 0) || 0 : 0);
  const perPage = 20;

  const kb = useListKeyboard({
    rowCount: rows.length,
    onOpen: (i) => { if (rows[i]) navigate(`/pharmacy/billing/${rows[i].id}`); },
    onCreate: () => navigate('/pharmacy/billing/new'),
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      writeParams({ search, status, page: page + 1 });
      const params = { page: page + 1, per_page: perPage };
      if (search) params.search = search;
      if (status) params.status = status;
      const res = await api.pharmacy.listBills(params);
      setRows(res?.rows ?? []);
      setTotal(res?.count ?? 0);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [page, search, status, selectedPharmacy, writeParams]);

  useEffect(() => { load(); }, [load]);

  const handleSearch = (e) => { setSearch(e.target.value); setPage(0); };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Card sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,121,142,0.10)' }}>
          {/* Header */}
          <Box sx={{ background: `linear-gradient(135deg, ${TEAL} 0%, #005f70 100%)`, px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <ReceiptIcon sx={{ color: '#fff', fontSize: 22 }} />
              <MDTypography variant="h6" sx={{ color: '#fff', fontWeight: 700, letterSpacing: 0.3 }}>
                Billing / Outward
              </MDTypography>
              <Chip label={`${total} bills`} size="small" sx={{ background: 'rgba(255,255,255,0.18)', color: '#fff', fontWeight: 600, fontSize: '0.7rem', height: 20 }} />
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                startIcon={<PaymentIcon />}
                onClick={() => navigate('/pharmacy/billing/payments')}
                sx={{ background: 'rgba(255,255,255,0.15)', color: '#fff', fontWeight: 600, fontSize: '0.73rem', px: 1.5, py: 0.6, borderRadius: 2, border: '1px solid rgba(255,255,255,0.3)', '&:hover': { background: 'rgba(255,255,255,0.25)', color: '#fff !important' } }}
              >
                Payments
              </Button>
              <Button
                startIcon={<ReceiptLongIcon />}
                onClick={() => navigate('/pharmacy/billing/credit-notes')}
                sx={{ background: 'rgba(255,255,255,0.15)', color: '#fff', fontWeight: 600, fontSize: '0.73rem', px: 1.5, py: 0.6, borderRadius: 2, border: '1px solid rgba(255,255,255,0.3)', '&:hover': { background: 'rgba(255,255,255,0.25)', color: '#fff !important' } }}
              >
                Credit Notes
              </Button>
              <Button
                startIcon={<NoteIcon />}
                onClick={() => navigate('/pharmacy/billing/debit-notes')}
                sx={{ background: 'rgba(255,255,255,0.15)', color: '#fff', fontWeight: 600, fontSize: '0.73rem', px: 1.5, py: 0.6, borderRadius: 2, border: '1px solid rgba(255,255,255,0.3)', '&:hover': { background: 'rgba(255,255,255,0.25)', color: '#fff !important' } }}
              >
                Debit Notes
              </Button>
              <Button
                startIcon={<PersonSearchIcon />}
                onClick={() => navigate('/pharmacy/billing/guest-patients')}
                sx={{ background: 'rgba(255,255,255,0.15)', color: '#fff', fontWeight: 600, fontSize: '0.73rem', px: 1.5, py: 0.6, borderRadius: 2, border: '1px solid rgba(255,255,255,0.3)', '&:hover': { background: 'rgba(255,255,255,0.25)', color: '#fff !important' } }}
              >
                Guest Patients
              </Button>
              <Button
                startIcon={<AddIcon />}
                onClick={() => navigate('/pharmacy/billing/new')}
                sx={{ background: '#fff', color: TEAL, fontWeight: 700, fontSize: '0.75rem', px: 2, py: 0.75, borderRadius: 2, '&:hover': { background: '#e0f4f7', color: TEAL } }}
              >
                New Bill (Alt+N)
              </Button>
            </Box>
          </Box>

          {/* Filters */}
          <Box sx={{ px: 2.5, py: 1.5, display: 'flex', gap: 2, alignItems: 'center', borderBottom: '1.5px solid #f0f2f5', flexWrap: 'wrap' }}>
            <TextField
              {...kb.searchProps}
              size="small" placeholder="Search bill no, patient, mobile…  ( / to focus )" value={search} onChange={handleSearch}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: '#aaa' }} /></InputAdornment> }}
              sx={{
                minWidth: 260,
                '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.82rem' },
                '& .MuiOutlinedInput-input': { py: '9.5px' },
              }}
            />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel sx={{ fontSize: '0.82rem' }}>Status</InputLabel>
              <Select
                value={status}
                label="Status"
                onChange={(e) => { setStatus(e.target.value); setPage(0); }}
                sx={{
                  fontSize: '0.82rem', borderRadius: 2,
                  '& .MuiSelect-select': {
                    py: '9.5px',
                    fontWeight: status ? 700 : 400,
                    color: status ? TEAL : 'inherit',
                  },
                }}
              >
                <MenuItem value=""><em style={{ color: '#aaa' }}>All statuses</em></MenuItem>
                {['DRAFT', 'FINALIZED', 'CANCELLED', 'PARTIALLY_RETURNED'].map(s => (
                  <MenuItem key={s} value={s} sx={{ fontSize: '0.82rem', py: 1 }}>{STATUS_LABEL[s]}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Table */}
          <TableContainer>
            <Table size="small">
              <TableRow>
                {['Bill No', 'Date', 'Patient', 'Doctor', 'Net Amount', 'Status', ''].map(h => (
                  <TableCell key={h} sx={TH}>{h}</TableCell>
                ))}
              </TableRow>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ py: 6 }}><CircularProgress size={28} sx={{ color: TEAL }} /></TableCell></TableRow>
                ) : rows.length === 0 ? (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ py: 6, color: '#aaa', fontSize: '0.82rem' }}>No bills found</TableCell></TableRow>
                ) : rows.map((r, i) => {
                  const last = i === rows.length - 1;
                  const rowProps = kb.getRowProps(i);
                  return (
                    <TableRow key={r.id} hover {...rowProps}
                      sx={{ cursor: 'pointer', ...(kb.activeIdx === i ? ACTIVE_ROW_SX : {}) }}
                      onClick={(e) => { rowProps.onClick(e); navigate(`/pharmacy/billing/${r.id}`); }}>
                      <TableCell sx={td(last)}>
                        <MDTypography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 700, color: TEAL }}>{r.bill_no}</MDTypography>
                      </TableCell>
                      <TableCell sx={td(last)}>{fmtDate(r.bill_date)}</TableCell>
                      <TableCell sx={td(last)}>
                        <Box sx={{ fontWeight: 600, fontSize: '0.8rem' }}>{r.patient_name}</Box>
                        {r.patient_mobile && <Box sx={{ fontSize: '0.72rem', color: '#888' }}>{r.patient_mobile}</Box>}
                      </TableCell>
                      <TableCell sx={td(last)}>
                        {r.is_self === 'Y' ? <span style={{ color: '#888', fontStyle: 'italic', fontSize: '0.78rem' }}>Self</span>
                          : r.doctor?.name ?? r.doctor_name ?? '—'}
                      </TableCell>
                      <TableCell sx={{ ...td(last), fontFamily: 'monospace', fontWeight: 700 }}>{fmt(r.net_amount)}</TableCell>
                      <TableCell sx={td(last)}>
                        <Chip label={STATUS_LABEL[r.status] ?? r.status} size="small" color={STATUS_COLOR[r.status] ?? 'default'}
                          sx={{ fontSize: '0.68rem', height: 20, fontWeight: 600 }} />
                      </TableCell>
                      <TableCell sx={td(last)}>
                        <IconButton size="small" sx={{ color: TEAL }} onClick={(e) => { e.stopPropagation(); navigate(`/pharmacy/billing/${r.id}`); }}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {total > perPage && (
            <Box sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'flex-end', gap: 1, borderTop: '1px solid #f0f2f5' }}>
              <Button size="small" disabled={page === 0} onClick={() => setPage(p => p - 1)} sx={{ fontSize: '0.75rem' }}>Prev</Button>
              <Box sx={{ fontSize: '0.78rem', color: '#555', alignSelf: 'center' }}>
                Page {page + 1} of {Math.ceil(total / perPage)}
              </Box>
              <Button size="small" disabled={(page + 1) * perPage >= total} onClick={() => setPage(p => p + 1)} sx={{ fontSize: '0.75rem' }}>Next</Button>
            </Box>
          )}
        </Card>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default BillingList;
